import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface NotificationSettings {
  email: {
    newPatron: boolean;
    newPost: boolean;
    newComment: boolean;
    newMessage: boolean;
    paymentSuccess: boolean;
    paymentFailed: boolean;
    subscriptionExpiring: boolean;
    monthlyReport: boolean;
    marketing: boolean;
    productUpdates: boolean;
  };
  push: {
    newPatron: boolean;
    newPost: boolean;
    newComment: boolean;
    newMessage: boolean;
    liveStream: boolean;
  };
  inApp: {
    all: boolean;
  };
}

const NotificationPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({
    email: {
      newPatron: true,
      newPost: true,
      newComment: true,
      newMessage: true,
      paymentSuccess: true,
      paymentFailed: true,
      subscriptionExpiring: true,
      monthlyReport: true,
      marketing: false,
      productUpdates: true,
    },
    push: {
      newPatron: true,
      newPost: false,
      newComment: true,
      newMessage: true,
      liveStream: true,
    },
    inApp: {
      all: true,
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `\${API_BASE_URL}/api/settings/notifications`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.settings) {
          setSettings(data.settings);
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `\${API_BASE_URL}/api/settings/notifications`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ settings }),
        }
      );

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEmailSetting = (key: keyof NotificationSettings['email']) => {
    setSettings({
      ...settings,
      email: { ...settings.email, [key]: !settings.email[key] },
    });
  };

  const updatePushSetting = (key: keyof NotificationSettings['push']) => {
    setSettings({
      ...settings,
      push: { ...settings.push, [key]: !settings.push[key] },
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          Notification Preferences
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Choose how you want to be notified about activity
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Email Notifications
          </h2>
          <div className="space-y-4">
            {Object.entries({
              newPatron: 'New patron subscription',
              newPost: 'New post from creators you support',
              newComment: 'New comment on your posts',
              newMessage: 'New direct messages',
              paymentSuccess: 'Successful payments',
              paymentFailed: 'Failed payments',
              subscriptionExpiring: 'Subscription expiring soon',
              monthlyReport: 'Monthly activity report',
              marketing: 'Marketing emails and promotions',
              productUpdates: 'Product updates and announcements',
            }).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{label}</span>
                <button
                  type="button"
                  onClick={() =>
                    updateEmailSetting(key as keyof NotificationSettings['email'])
                  }
                  className={`${
                    settings.email[key as keyof NotificationSettings['email']]
                      ? 'bg-indigo-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      settings.email[key as keyof NotificationSettings['email']]
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Push Notifications
          </h2>
          <div className="space-y-4">
            {Object.entries({
              newPatron: 'New patron subscription',
              newPost: 'New post from creators',
              newComment: 'New comment on your posts',
              newMessage: 'New direct messages',
              liveStream: 'Creator goes live',
            }).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{label}</span>
                <button
                  type="button"
                  onClick={() =>
                    updatePushSetting(key as keyof NotificationSettings['push'])
                  }
                  className={`${
                    settings.push[key as keyof NotificationSettings['push']]
                      ? 'bg-indigo-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      settings.push[key as keyof NotificationSettings['push']]
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* In-App Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            In-App Notifications
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">
                Enable all in-app notifications
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Show notification badges and alerts while using the platform
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setSettings({
                  ...settings,
                  inApp: { all: !settings.inApp.all },
                })
              }
              className={`${
                settings.inApp.all ? 'bg-indigo-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  settings.inApp.all ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>

        {success && (
          <div className="flex items-center justify-center p-4 bg-green-50 rounded-md">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-800">
              Notification preferences saved successfully!
            </span>
          </div>
        )}
      </form>
    </div>
  );
};

export default NotificationPreferencesPage;
